'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { t } from '@lingui/macro';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, type StackProps, Tooltip } from '@mui/material';
import {
  Button,
  type ButtonProps,
  DatePickerInput,
  FavoriteRadioGroup,
  TextFieldInput,
} from '@prismo-io/design-system';
import { FormationCreatedFrom } from '@prismo-io/schemas';
import {
  type FC,
  type ReactNode,
  useCallback,
  useMemo,
  useTransition,
} from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useDeleteFormation } from '../../../hooks/use-delete-formation';
import { useSendFormation } from '../../../hooks/use-send-formation';
import { AppellationsAutocompleteInput } from '../../inputs/appellations/appellations-autocomplete';
import { FormationLevelsAutocompleteInput } from '../../inputs/formation-levels/formation-levels-autocomplete';
import {
  FormationSchema,
  type FormationSchemaNullableT,
  type FormationSchemaT,
} from './schema';

type FormationFormProps = {
  defaultValues?: Partial<FormationSchemaT>;
  onComplete?: SubmitHandler<FormationSchemaT>;
  onDelete?: () => void;
  withFavorite?: boolean;
  needResetAfterSubmit?: boolean;
  submitProps?: {
    button?: ButtonProps;
    alignItem?: StackProps['alignItems'];
    justifyContent?: StackProps['justifyContent'];
  };
  submitBtns?: ReactNode;
  marginY?: number;
  needDeleteBtn?: boolean;
};

export const FormationForm: FC<FormationFormProps> = (props) => {
  const {
    defaultValues = {} as FormationSchemaT,
    onComplete = undefined,
    onDelete = undefined,
    withFavorite = false,
    needResetAfterSubmit = true,
    submitProps = {},
    submitBtns = undefined,
    marginY = 0,
    needDeleteBtn = false,
  } = props;

  const {
    button = { children: t`+ Ajouter` },
    alignItem = 'center',
    justifyContent = 'end',
  } = submitProps;

  const emptyValuesForm = {
    id: null,
    name: '',
    appellations: [],
    level: null,
    establishment: '',
    favorite: null,
    yearStart: null,
    yearEnd: null,
    createdFrom: FormationCreatedFrom.Enum.PRISMO,
  };

  const formMethods = useForm<FormationSchemaNullableT>({
    mode: 'all',
    resolver: zodResolver(FormationSchema()),
    defaultValues: { ...emptyValuesForm, ...defaultValues },
  });

  const {
    control,
    reset,
    watch,
    formState: { isValid, isSubmitting, isSubmitSuccessful },
    setValue,
  } = formMethods;

  const { trigger: deleteFormation } = useDeleteFormation();

  const onDeleteItem = () => {
    // const validData = FormationSchema.parse(defaultValues);
    if (defaultValues.id) {
      deleteFormation(defaultValues.id, {
        onSuccess: async () => {
          startTransition(() => {
            reset(emptyValuesForm);
            if (onDelete) onDelete();
          });
        },
      });
    }
  };

  const { trigger: sendFormation } = useSendFormation();

  const [isTransiting, startTransition] = useTransition();

  const isLoading = useMemo(
    () => isTransiting || isSubmitting,
    [isTransiting, isSubmitting]
  );

  const onSubmit: SubmitHandler<FormationSchemaNullableT> = (data) => {
    const validData = FormationSchema().parse(data);

    const {
      id,
      name,
      level,
      establishment,
      favorite,
      yearStart,
      yearEnd,
      appellations,
      createdFrom,
    } = validData;

    if (!level) {
      throw new Error('t');
    }

    return sendFormation(
      {
        id: id || null,
        name,
        level_id: level.id || null,
        establishment: establishment || null,
        favorite: favorite ? Number.parseInt(favorite) : null,
        yearStart: yearStart?.toDateString() || null,
        yearEnd: yearEnd?.toDateString() || null,
        designations: appellations.map((app) => {
          return {
            code: app.code,
            referential_id: app.referential_id,
          };
        }),
        createdFrom,
      },
      {
        onSuccess: async (res) => {
          setValue('id', res.id);

          const eventName = id ? 'formation_updated' : 'formation_created';

          startTransition(() => {
            if (!id) {
              reset(emptyValuesForm);
            }
            if (onComplete) {
              onComplete({ id: res.id, ...validData });
            }
          });
        },
      }
    );
  };

  const startAt = watch('yearStart');
  const endAt = watch('yearEnd');

  const shouldDisableYearStart = useCallback(
    (date: Date) => {
      if (!endAt || !date) {
        return false;
      }

      const yearEnd = new Date(endAt).getFullYear();
      const year = date.getFullYear();

      return year > yearEnd;
    },
    [endAt]
  );

  const shouldDisableMonthStart = useCallback(
    (date: Date) => {
      if (!endAt || !date) {
        return false;
      }

      const yearEnd = new Date(endAt).getFullYear();
      const year = date.getFullYear();

      const monthEnd = new Date(endAt).getMonth();
      const month = date.getMonth();

      return yearEnd === year && month > monthEnd;
    },
    [endAt]
  );

  const shouldDisableYearEnd = useCallback(
    (date: Date) => {
      if (!startAt || !date) {
        return false;
      }

      const yearStart = new Date(startAt).getFullYear();
      const year = date.getFullYear();

      return year < yearStart;
    },
    [startAt]
  );

  const shouldDisableMonthEnd = useCallback(
    (date: Date) => {
      if (!startAt || !date) {
        return false;
      }

      const yearStart = new Date(startAt).getFullYear();
      const year = date.getFullYear();

      const monthStart = new Date(startAt).getMonth();
      const month = date.getMonth();

      return yearStart === year && month < monthStart;
    },
    [startAt]
  );

  return (
    <FormProvider {...formMethods}>
      <Stack
        spacing={2}
        component="form"
        onSubmit={formMethods.handleSubmit(onSubmit)}
      >
        <TextFieldInput
          control={control}
          name="name"
          label={t`Intitulé de la formation`}
          disabled={isLoading}
          required
        />
        <FormationLevelsAutocompleteInput
          control={control}
          name="level"
          label={t`Niveau d'étude`}
          disabled={isLoading}
          required
        />
        <TextFieldInput
          control={control}
          name="establishment"
          label={t`Nom de l'établissement`}
          disabled={isLoading}
        />
        <Stack direction="row" justifyContent="space-between">
          <DatePickerInput
            control={control}
            name="yearStart"
            label={t`Date de début`}
            views={['year', 'month']}
            disabled={isLoading}
            disableFuture
            shouldDisableYear={shouldDisableYearStart}
            shouldDisableMonth={shouldDisableMonthStart}
          />
          <DatePickerInput
            control={control}
            name="yearEnd"
            label={t`Date de fin`}
            views={['year', 'month']}
            disabled={isLoading}
            shouldDisableYear={shouldDisableYearEnd}
            shouldDisableMonth={shouldDisableMonthEnd}
          />
        </Stack>
        <Tooltip
          title={t`Commencez à écrire les premières lettres de votre métier, pour le trouver.`}
          placement="top-end"
        >
          <Stack>
            <AppellationsAutocompleteInput
              control={control}
              name="appellations"
              label={t`Pour préparer les métiers`}
              disabled={isLoading}
              required
              disableCloseOnSelect
            />
          </Stack>
        </Tooltip>
        {withFavorite && (
          <FavoriteRadioGroup
            control={control}
            name="favorite"
            label={t`Favori`}
            disabled={isLoading}
          />
        )}
        {!!submitBtns ? (
          submitBtns
        ) : (
          <Stack
            direction="row"
            justifyContent={justifyContent}
            alignItems={alignItem}
          >
            {needDeleteBtn && (
              <Tooltip title={t`Supprimer la formation`} placement="right">
                <IconButton
                  aria-label="delete"
                  type="button"
                  onClick={onDeleteItem}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="contained"
              color="gradient"
              {...button}
              type="submit"
              loading={isLoading}
              disabled={!isValid || isLoading}
            />
          </Stack>
        )}
      </Stack>
    </FormProvider>
  );
};
